import {
  DataGrid as MuiDataGrid,
  GridActionsCellItem,
  GridCellParams,
  GridRowParams,
} from '@mui/x-data-grid';
import { Avatar, styled, Box, Stack, Link } from '@mui/material';
import PreviewIcon from '@mui/icons-material/Preview';
import DoneIcon from '@mui/icons-material/Done';
import ClearIcon from '@mui/icons-material/Clear';
import { cloneConfiguration } from '../../api/ConfigurationApi';
import Tooltip from '@mui/material/Tooltip';
import { settings } from '../../settings';
import { useTranslation } from 'react-i18next';
import { EUrlParams } from '../../data/Constants';
import { ICloneConfigurationRequest } from '../../../types/IApiTypes';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from 'store/AppStore';
import { formatDateString } from '../../services/DetailsPageHelper';
import { ITranslateProps } from '../../../types';
import { setSearchValue } from '../../store/slice/ConfigurationSlice';

const DataGrid = styled(MuiDataGrid)(() => ({
  '& .MuiDataGrid-columnHeaderTitle': { fontSize: '1.25rem', fontWeight: 'normal' },
  '& .MuiDataGrid-cell': { fontSize: 14 },
}));

const renderNoRowsMessage = (t: ITranslateProps) => {
  return (
    <Stack sx={{ height: '100%', alignItems: 'center', justifyContent: 'center' }}>
      {t('label.noRows')}
    </Stack>
  );
};

export default function DataTableGrid() {
  const t = useTranslation().t;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const configuration = useSelector((state: AppState) => state.configuration);

  const handleOnCellClick = (currentSelection: GridCellParams) => {
    const buttonField = currentSelection.field;
    if (buttonField != null || buttonField != undefined) {
      switch (buttonField) {
        case 'Clone': {
          handleClone(currentSelection);
          break;
        }
        case 'View': {
          handleView(currentSelection);
          break;
        }
        default: {
          break;
        }
      }
    }
  };

  const gridColumns = () => {
    return [
      {
        field: 'productId',
        headerName: t('label.product'),
        flex: 1.5,
        editable: false,
        headerClassName: 'lastcolumnSeparator',
        minWidth: 150,
        renderCell: (params: GridRowParams) => {
          return params.row.productId + ' (' + params.row.productName + ')';
        },
      },
      {
        field: 'configurationId',
        headerName: t('label.configurationId'),
        flex: 2,
        editable: false,
        headerClassName: 'lastcolumnSeparator',
        minWidth: 150,
        renderCell: (params: GridRowParams) => {
          return configurationIdColumn(handleClone, params);
        },
      },
      {
        field: 'country',
        headerName: t('label.country'),
        flex: 1,
        editable: false,
        headerClassName: 'lastcolumnSeparator',
        renderCell: (params: GridRowParams) => {
          return params.row.countryName + ' (' + params.row.countryCode + ')';
        },
      },
      {
        field: 'createdOn',
        headerName: t('label.createdOn'),
        flex: 1,
        editable: false,
        headerClassName: 'lastcolumnSeparator',
        minWidth: 150,
        renderCell: (params: GridRowParams) => {
          return formatDateString(params.row.createdOn) + t('label.timeZone');
        },
      },
      {
        field: 'modifiedOn',
        headerName: t('label.modifiedOn'),
        flex: 1,
        editable: false,
        headerClassName: 'lastcolumnSeparator',
        minWidth: 150,
        renderCell: (params: GridRowParams) => {
          return formatDateString(params.row.modifiedOn) + t('label.timeZone');
        },
      },
      {
        field: 'isValid',
        type: 'actions',
        headerName: t('label.validate'),
        flex: 0.5,
        headerClassName: 'lastcolumnSeparator',
        minWidth: 140,
        getActions: (params: GridRowParams) => [
          dataGridValidateColumn(params, t('tooltip.valid'), t('tooltip.invalid')),
        ],
      },
      {
        field: 'View',
        type: 'actions',
        headerName: t('label.view'),
        flex: 0.5,
        headerClassName: 'lastcolumnSeparator',
        minWidth: 70,
        getActions: (params: GridRowParams) => [
          <Tooltip
            key={params?.id}
            title={t('tooltip.view')}
            placement="left"
            slotProps={{ popper: { disablePortal: true } }}
          >
            <GridActionsCellItem
              icon={
                <Avatar sx={{ width: 30, height: 30, backgroundColor: '#5e5c5c !important' }}>
                  <PreviewIcon />
                </Avatar>
              }
              label={t('tooltip.view')}
            />
          </Tooltip>,
        ],
      },
    ];
  };

  const handleClone = async (currentSelection: GridCellParams) => {
    const bodyFormData: ICloneConfigurationRequest = {
      configurationId: String(currentSelection.row.configurationId),
      state: '2',
    };

    const response = await cloneConfiguration(bodyFormData);
    if (response?.data) {
      const queryParams = new URLSearchParams();
      queryParams.append(EUrlParams.ConfigurationId, response.data);
      queryParams.append(EUrlParams.Scope, settings.Context);

      const url = settings.ConfiguratorEndpoint + `/?${queryParams}`;
      window.open(url, '_blank');
    }
  };

  const handleView = (currentSelection: GridCellParams) => {
    dispatch(setSearchValue(''));
    const configId = currentSelection.row.configurationId;
    const productId = currentSelection.row.productId;
    const defaultPageUrl = '/' + productId + '/';
    navigate(defaultPageUrl + configId, { state: { configId: configId } });
  };

  const dataGridColumns = gridColumns();

  return (
    <Box className="cofiguration-data-table">
      <DataGrid
        rows={configuration.configurations}
        columns={dataGridColumns}
        getRowId={(row) => {
          //To set key to row
          return row['configurationId'];
        }}
        onCellClick={handleOnCellClick}
        rowHeight={36}
        hideFooter={true}
        headerHeight={36}
        disableColumnMenu
        initialState={{
          sorting: {
            sortModel: [{ field: 'timeStamp', sort: 'desc' }],
          },
        }}
        slots={{
          noRowsOverlay: () => renderNoRowsMessage(t),
        }}
        sx={{
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#eeeeee',
            color: '#000000',
          },
          '& .MuiDataGrid-columnSeparator': {
            display: 'none',
          },
          boxShadow: 2,
        }}
      />
    </Box>
  );
}

function configurationIdColumn(handleClone: object, params: GridRowParams) {
  return (
    <Link
      onClick={() => {
        handleClone(params);
      }}
      style={{ cursor: 'pointer' }}
    >
      {params.row.configurationId}
    </Link>
  );
}

function dataGridValidateColumn(params: GridRowParams, tValid: string, tInvalid: string) {
  return (
    <Tooltip
      title={params.row.isValid ? tValid : tInvalid}
      placement="left"
      slotProps={{ popper: { disablePortal: true } }}
    >
      <GridActionsCellItem
        sx={{ width: 30, height: 30 }}
        icon={
          <Avatar sx={{ width: 30, height: 30, background: 'none' }}>
            {params.row.isValid ? (
              <DoneIcon className="valid-icon" />
            ) : (
              <ClearIcon className="invalid-icon" />
            )}
          </Avatar>
        }
        label=""
      />
    </Tooltip>
  );
}
